import { isDormant, dormancyLabel, KIND } from './entityDormancy';

// 684a — IS THIS STORAGE A LEGAL DESTINATION? One definition.
//
// 684's census found the B2 defect: the entry/receipt pickers applied NO lifecycle filter while the
// server DOES refuse the post (deactivatedEntityError, server.js ~1100, "Build 93 R1", resolves
// storage.is_inactive / is_decommissioned → site.is_archived). So a decommissioned storage was
// offered, selected, and the whole form failed at submit. That is worse than hiding it: the user
// finds out last.
//
// The rule already existed in exactly one place — ReviewPage:257:
//     setStorages((data || []).filter(st => !st.is_inactive && !st.is_decommissioned));
// This module is that rule, hoisted so the four pickers cannot drift from it (the 662/664/674/681
// twin-divergence lesson).
//
// 684b — NOW BACKED BY entityDormancy. 684a defined this rule locally to ship the picker fix without
// pre-empting the KIND.STORAGE design; 684b landed that KIND, so the seam is closed and the rule is
// no longer a second definition — it delegates. What remains here is the SELECTION semantics (a
// picker's "may I choose this?"), which is a different question from "is it dormant?" even though
// today they coincide: dormancy now also covers a storage on an ARCHIVED SITE, which the old local
// predicate missed entirely.

/**
 * A storage may be chosen only while it is not dormant.
 * Dormant = decommissioned (terminal) | on an archived site | deactivated — see dormancyReason.
 */
export const isStorageSelectable = (s) => !!s && !isDormant(s, KIND.STORAGE);

/**
 * Filter a storage list for a picker.
 *
 * `alwaysShowId` mirrors entityDormancy.isVisible: the currently-BOUND storage stays in the list
 * even when it is no longer selectable, so opening an existing line whose storage was since
 * deactivated does not silently blank the field (and does not look like the goods have no
 * destination). The user can still see where it points; they just cannot pick it for a new line.
 *
 * @param {Array}  list
 * @param {string} [alwaysShowId]  the line's current storage_id
 */
export function selectableStorages(list, alwaysShowId = null) {
  return (list || []).filter(s => isStorageSelectable(s) || (alwaysShowId != null && s && s.id === alwaysShowId));
}

/**
 * 687 — the option LABEL for a storage in a picker.
 *
 * A storage only survives selectableStorages while dormant when it is the line's BOUND one
 * (alwaysShowId). Rendering it with a bare name would be its own small lie: it looks selectable and
 * identical to the live options, while the server would refuse a post to it. So it carries its
 * reason — "Feed Store — Decommissioned" — the same label-suffix affordance used for bands in
 * CorrectionEditControl (686) and for farms in CostAllocationReport / ExpenseReallocationPage.
 *
 * Live storages are returned unchanged, so nothing else in the list moves.
 */
export function storageOptionLabel(s) {
  if (!s) return '';
  const reason = dormancyLabel(s, KIND.STORAGE);
  return reason ? `${s.name} — ${reason}` : s.name;
}
